//  ----------      START   IMPORT  BLOCK       ----------
import { Row, Col, Form, Button, Card, ListGroup } from "react-bootstrap";
import { Trash, Plus } from "react-bootstrap-icons";
// Context
import { useLanguage } from "../../../../../API/Context/LanguageContext";
// Config
import VocabWorkingHours from "../../../../../ConfigFiles/multilingual/VocabWorkingHours";
import { VocabButtons as btn_vocab } from "../../../../../ConfigFiles/multilingual/VocabButtons";
import { get_weekdays } from "../../../../../ConfigFiles/multilingual/VocabWeekdays";
// Helpers
import { formatTime, buildDateTime } from "../../../../../Utils/timeUtils.ts";
//  ----------      END   IMPORT  BLOCK       ----------
// ... (Imports remain the same)
import { Copy, ExclamationTriangleFill } from "react-bootstrap-icons";

const StepWorkingHours = ({ data, update }) => {
    const { lan } = useLanguage();
    const weekdays = get_weekdays(lan);

    const updateSchedule = (field, value) => {
        update({
            working_hours: { ...data.working_hours, [field]: value }
        });
    };

    const updateDayIntervals = (dayIdx, newIntervals) => {
        const newWeekly = [...data.working_hours.weekly_schedule];
        newWeekly[dayIdx] = newIntervals;
        updateSchedule('weekly_schedule', newWeekly);
    };

    // 🎯 Logic Preserved: Copy Monday's intervals to Tuesday-Friday
    const copyMondayToWorkdays = () => {
    const mondayIntervals = data.working_hours.weekly_schedule[0];
    
    // Use map and object spread to preserve the Date object instances
    const newWeekly = data.working_hours.weekly_schedule.map((day, idx) => 
        (idx > 0 && idx < 5) 
            ? mondayIntervals.map(slot => ({ ...slot }))
            : day
    );
    updateSchedule('weekly_schedule', newWeekly);
};

    const addInterval = (dayIdx) => {
        const current = data.working_hours.weekly_schedule[dayIdx];
        const newInterval = { 
            start: buildDateTime("09:00"), 
            end: buildDateTime("17:00") 
        };
        updateDayIntervals(dayIdx, [...current, newInterval]);
    };

    const removeInterval = (dayIdx, intervalIdx) => {
        const filtered = data.working_hours.weekly_schedule[dayIdx].filter((_, i) => i !== intervalIdx);
        updateDayIntervals(dayIdx, filtered);
    };

    const handleTimeChange = (dayIdx, intervalIdx, field, timeString) => {
        const dayIntervals = [...data.working_hours.weekly_schedule[dayIdx]];
        dayIntervals[intervalIdx] = {
            ...dayIntervals[intervalIdx],
            [field]: buildDateTime(timeString)
        };
        updateDayIntervals(dayIdx, dayIntervals);
    };

    // 🎯 Logic Preserved: Validation check
    const isInvalidRange = (start, end) => {
        return new Date(start) >= new Date(end);
    };

    return (
        <div className="animate-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="border-start border-primary border-4 ps-3 mb-0">
                    {VocabWorkingHours.working_hours[lan]}
                </h4>
                {/* Copy Button */}
                <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={copyMondayToWorkdays}
                    disabled={data.working_hours.weekly_schedule[0].length === 0}
                >
                    <Copy className="me-2" />
                    {lan === 0 ? "Copiar Lunes a Viernes" : "Copy Mon to Fri"}
                </Button>
            </div>

            {/* Global Settings */}
            <Card className="mb-4 border-0 bg-light shadow-sm p-3">
                <Row>
                    <Col md={6}>
                        <Form.Check 
                            type="switch"
                            id="manage-agenda-switch"
                            label={VocabWorkingHours.manage_my_agenda[lan]}
                            checked={data.working_hours.manage_agenda}
                            onChange={e => updateSchedule('manage_agenda', e.target.checked)}
                        />
                    </Col>
                    <Col md={6}>
                        <Form.Check 
                            type="switch"
                            id="visibility-switch"
                            label={VocabWorkingHours.make_schedule_visible[lan]}
                            checked={data.working_hours.schedule_visible}
                            onChange={e => updateSchedule('schedule_visible', e.target.checked)}
                        />
                    </Col>
                </Row>
            </Card>

            <ListGroup variant="flush" className="rounded-3 shadow-sm border">
                {weekdays.map((dayName, dayIdx) => (
                    <ListGroup.Item key={dayIdx} className="py-3">
                        <Row className="align-items-center">
                            <Col md={3}>
                                <span className="fw-bold">{dayName}</span>
                            </Col>
                            <Col md={7}>
                                {data.working_hours.weekly_schedule[dayIdx].length === 0 ? (
                                    <small className="text-muted italic">{VocabWorkingHours.no_assigned_schedules[lan]}</small>
                                ) : (
                                    data.working_hours.weekly_schedule[dayIdx].map((interval, iIdx) => {
                                        const error = isInvalidRange(interval.start, interval.end);
                                        return (
                                            <div key={iIdx} className="mb-2">
                                                <div className="d-flex gap-2 align-items-center">
                                                    <Form.Control 
                                                        size="sm"
                                                        type="time" 
                                                        isInvalid={error}
                                                        value={formatTime(interval.start)}
                                                        onChange={e => handleTimeChange(dayIdx, iIdx, 'start', e.target.value)}
                                                    />
                                                    <span>-</span>
                                                    <Form.Control 
                                                        size="sm"
                                                        type="time" 
                                                        isInvalid={error}
                                                        value={formatTime(interval.end)}
                                                        onChange={e => handleTimeChange(dayIdx, iIdx, 'end', e.target.value)}
                                                    />
                                                    <Button 
                                                        variant="link" 
                                                        className="text-danger p-0 ms-2"
                                                        onClick={() => removeInterval(dayIdx, iIdx)}
                                                    >
                                                        <Trash size={14}/>
                                                    </Button>
                                                </div>
                                                {error && (
                                                    <div className="text-danger smaller mt-1 d-flex align-items-center" style={{ fontSize: '11px' }}>
                                                        <ExclamationTriangleFill className="me-1" />
                                                        {lan === 0 ? "La hora de fin debe ser posterior" : "End must be after start"}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </Col>
                            {/* 🎯 Reverted back to the original Col/Button layout */}
                            <Col md={2} className="text-end">
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => addInterval(dayIdx)}
                                >
                                    <Plus className="me-1"/> {btn_vocab.add[lan]}
                                </Button>
                            </Col>
                        </Row>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </div>
    );
};
export default StepWorkingHours;